/**
 * components/shared/BrokerSelector/BrokerSelector.tsx
 * Main reusable broker selection component with HeroUI
 * 
 * Clean HeroUI Listbox implementation combining search, filtering, and display logic.
 * Uses HeroUI Listbox for optimal selection UX and efficient space usage.
 * Much more compact and professional than card-based approach.
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Users, Sparkles, Building, Check, X, AlertTriangle, DollarSign } from 'lucide-react';

// 2. Internal absolute imports
import type { AssetClassType } from '@/types/shared/AssetClass';
import { useBrokerSelection, type BrokerSelectionFilterOptions } from '@/hooks/useBrokerSelection';
import { Listbox, ListboxItem, Chip, Button, Checkbox } from '@enfomentis/ui';
import type { BrokerCatalogEntry } from '@/types/reference/BrokerCatalog';
import { FeeDetailModal } from '@/components/shared/BrokerFees/FeeDetailModal';

// 3. Relative imports
import { BrokerSearch } from './BrokerSearch';

/**
 * Props for BrokerSelector component
 */
export interface BrokerSelectorProps {
  /** Currently selected broker ID */
  selectedBrokerId?: string;
  
  /** Broker selection handler */
  onBrokerSelect: (brokerId: string) => void;
  
  /** Filtering options */
  filterOptions?: BrokerSelectionFilterOptions;
  
  /** Asset classes to show support indicators for */
  relevantAssets?: AssetClassType[];
  
  /** Whether to show fee previews on broker cards */
  showFees?: boolean;
  
  /** Whether to show asset support indicators */
  showAssetIndicators?: boolean;
  
  /** Search placeholder text */
  searchPlaceholder?: string;
  
  /** Whether the selector is disabled */
  disabled?: boolean;
  
  
  
  /** Whether to show custom broker card */
  showCustomBroker?: boolean;
  
  /** Existing custom broker data (for edit mode) */
  customBroker?: {
    name: string;
    id: string;
  } | undefined;
}


/**
 * Empty state component
 */
const EmptyState: React.FC<{
  searchQuery: string;
  hasFilters: boolean;
  showToggle?: boolean;
}> = ({ searchQuery, hasFilters, showToggle }) => (
  <div className="text-center py-6">
    <p className="text-default-500 text-sm max-w-sm mx-auto">
      {searchQuery ? (
        <>Try adjusting your search terms or clearing the search to see all available brokers.</>
      ) : hasFilters ? (
        showToggle ? (
          <>No brokers match all your criteria. Toggle "Show all brokers" below to see brokers that support some of your selected assets.</>
        ) : (
          <>No brokers match your current criteria. Try adjusting your filters.</>
        )
      ) : (
        <>There are no brokers available for selection at this time.</>
      )}
    </p>
  </div>
);


/**
 * Asset support indicator component
 */
const AssetIndicator: React.FC<{
  assetType: AssetClassType;
  isSupported: boolean;
  isPartialSupport?: boolean;
}> = ({ assetType, isSupported, isPartialSupport }) => {
  const getIcon = (): React.ReactNode => {
    if (isSupported && !isPartialSupport) {
      return <Check className="w-3 h-3" />;
    }
    if (isPartialSupport) {
      return <AlertTriangle className="w-3 h-3" />;
    }
    return <X className="w-3 h-3" />;
  };

  const getLabel = (): string => {
    switch (assetType) {
      case 'futures': return 'Futures';
      case 'stocks': return 'Stocks';
      case 'forex': return 'Forex';
      case 'crypto': return 'Crypto';
      case 'options': return 'Options';
      case 'cfd': return 'CFD';
      case 'bonds': return 'Bonds';
      default: return String(assetType).charAt(0).toUpperCase() + String(assetType).slice(1);
    }
  };

  return (
    <Chip
      size="sm"
      variant={isSupported ? "flat" : "bordered"}
      color={isSupported ? (isPartialSupport ? "warning" : "success") : "default"}
      startContent={getIcon()}
      className="text-xs"
    >
      {getLabel()}
    </Chip>
  );
};

/**
 * Main reusable broker selector component with HeroUI
 */
export const BrokerSelector: React.FC<BrokerSelectorProps> = ({
  selectedBrokerId,
  onBrokerSelect,
  filterOptions = {},
  relevantAssets = [],
  showFees = true,
  showAssetIndicators = true,
  searchPlaceholder = 'Search brokers...',
  disabled = false,
  showCustomBroker = false,
  customBroker
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllBrokers, setShowAllBrokers] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [selectedBrokerForFees, setSelectedBrokerForFees] = useState<BrokerCatalogEntry | null>(null);
  // Convert selectedBrokerId to Set for HeroUI Listbox
  const selectedKeys = useMemo(() => {
    return selectedBrokerId ? new Set([selectedBrokerId]) : new Set();
  }, [selectedBrokerId]);
  
  // Track if this is initial load to prevent clearing selection
  const isInitialLoad = useRef(true);

  // Get broker data from hook
  const {
    all,
    recommended,
    hasFilters,
    totalCount
  } = useBrokerSelection({
    ...filterOptions,
    searchQuery,
    showAllBrokers: showAllBrokers
  });

  // Derived state
  // const hasRecommended = recommended.length > 0;
  const hasResults = all.length > 0;
  const filtered = searchQuery ? all.filter(broker => 
    broker.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) : all;

  // Determine which brokers to show in each section
  const recommendedBrokers = searchQuery ? filtered.filter((broker: BrokerCatalogEntry) =>
    recommended.some(r => r.id === broker.id)
  ) : recommended;

  const allBrokers = searchQuery ? filtered : all;

  // Show toggle if there are more brokers in the catalog than currently recommended
  // This means there are additional brokers that could be shown if filters are relaxed
  const showToggle = recommended.length < totalCount && !searchQuery;

  // Handle broker selection from Listbox - following HeroUI docs pattern
  const handleSelectionChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys !== 'all' && keys.size > 0) {
      const selectedKey = Array.from(keys)[0] as string;
      // Call parent callback for persistence
      onBrokerSelect(selectedKey);
    }
  };

  // Clear selection if broker is not in available list (on filter change)
  useEffect(() => {
    if (!isInitialLoad.current && selectedBrokerId) {
      // Clear selection if not in recommended list and not custom broker
      if (selectedBrokerId && !recommended.find(b => b.id === selectedBrokerId) && selectedBrokerId !== 'custom-broker') {
        onBrokerSelect('');
      }

      isInitialLoad.current = false;
    }
  }, [recommended, selectedBrokerId, onBrokerSelect]);


  // Handle fee details modal
  const handleViewFees = useCallback((broker: BrokerCatalogEntry): void => {
    setSelectedBrokerForFees(broker);
    setShowFeeModal(true);
  }, []);

  // Create enhanced content for broker items
  const createBrokerContent = useCallback((broker: BrokerCatalogEntry) => {
    const assetIndicators = showAssetIndicators && relevantAssets.length > 0 ? (
      <div className="flex items-center flex-wrap gap-1 mt-1">
        {relevantAssets.slice(0, 3).map(assetClass => (
          <AssetIndicator
            key={assetClass}
            assetType={assetClass}
            isSupported={broker.assetClasses.includes(assetClass)}
          />
        ))}
        {relevantAssets.length > 3 && (
          <Chip size="sm" variant="bordered" className="text-xs">
            +{relevantAssets.length - 3} more
          </Chip>
        )}
      </div>
    ) : null;

    const feeButton = showFees ? (
      <Button
        size="sm"
        variant="light"
        color="primary"
        startContent={<DollarSign className="w-3 h-3" />}
        onPress={() => handleViewFees(broker)}
        className="h-6 px-2 text-xs mt-1"
      >
        View Fees
      </Button>
    ) : null;

    return (
      <div className="w-full">
        <div className="flex items-center justify-between">
          <span className="font-medium">{broker.name}</span>
          <Building className="w-4 h-4 text-default-500" />
        </div>
        {(assetIndicators || feeButton) && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex-1">{assetIndicators}</div>
            {feeButton}
          </div>
        )}
      </div>
    );
  }, [showAssetIndicators, relevantAssets, showFees, handleViewFees]);

  // Create data arrays for HeroUI Listbox collections
  const recommendedBrokerItems = useMemo(() => {
    const items: Array<{
      key: string;
      broker: BrokerCatalogEntry | null;
      textValue: string;
      content: React.ReactNode;
    }> = recommendedBrokers.map(broker => ({
      key: broker.id,
      broker: broker,
      textValue: broker.name,
      content: createBrokerContent(broker)
    }));
    
    if (showCustomBroker) {
      items.push({
        key: 'custom-broker',
        broker: null,
        textValue: customBroker ? customBroker.name : 'Create Custom Broker',
        content: (
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="font-medium">{customBroker ? customBroker.name : 'Create Custom Broker'}</span>
              <div className="text-xs text-default-500 mt-1">Configure fees for brokers not in our catalog</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
          </div>
        )
      });
    }
    
    return items;
  }, [recommendedBrokers, showCustomBroker, customBroker, createBrokerContent]);

  const allBrokerItems = useMemo(() => {
    const items: Array<{
      key: string;
      broker: BrokerCatalogEntry | null;
      textValue: string;
      content: React.ReactNode;
    }> = allBrokers.map(broker => ({
      key: broker.id,
      broker: broker,
      textValue: broker.name,
      content: createBrokerContent(broker)
    }));
    
    if (showCustomBroker) {
      items.push({
        key: 'custom-broker',
        broker: null,
        textValue: customBroker ? customBroker.name : 'Create Custom Broker',
        content: (
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="font-medium">{customBroker ? customBroker.name : 'Create Custom Broker'}</span>
              <div className="text-xs text-default-500 mt-1">Configure fees for brokers not in our catalog</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
          </div>
        )
      });
    }
    
    return items;
  }, [allBrokers, showCustomBroker, customBroker, createBrokerContent]);


  return (
    <div className="space-y-4">
      {/* Search */}
      <BrokerSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={searchPlaceholder}
        disabled={disabled}
      />

      {/* Single Broker List */}
      <div className="space-y-4">
        {hasResults ? (
          <div>
            {/* Header with integrated toggle */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2">
                {showAllBrokers ? 
                  <Users className="w-5 h-5 text-default-400" /> : 
                  <Sparkles className="w-5 h-5 text-primary" />
                }
                <h3 className="font-medium text-foreground">
                  {showAllBrokers ? 
                    "All Brokers" : 
                    (filterOptions.accountType === 'paper' ? "Paper Trading" : "Recommended")
                  } ({showAllBrokers ? allBrokers.length : recommendedBrokers.length})
                </h3>
              </div>
              
              {/* Integrated Show All Toggle */}
              {showToggle && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    isSelected={showAllBrokers}
                    onValueChange={setShowAllBrokers}
                    isDisabled={disabled}
                    size="sm"
                  >
                    <span className="text-sm text-foreground select-none">
                      Show all brokers
                    </span>
                  </Checkbox>
                </div>
              )}
            </div>
            
            <div className="border border-divider rounded-lg mt-4">
              <Listbox 
                aria-label={showAllBrokers ? "All available brokers" : "Recommended brokers"}
                variant="flat"
                color="primary"
                selectionMode="single"
                selectedKeys={selectedKeys as Set<string>}
                onSelectionChange={handleSelectionChange}
                disallowEmptySelection
                shouldHighlightOnFocus={true}
                className="max-h-[400px] overflow-y-auto"
                {...(disabled && { disableKeys: ['all'] })}
              >
                {(showAllBrokers ? allBrokerItems : recommendedBrokerItems).map(item => (
                  <ListboxItem
                    key={item.key}
                    textValue={item.textValue}
                    className="py-3 data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
                  >
                    {item.content}
                  </ListboxItem>
                ))}
              </Listbox>
            </div>
          </div>
        ) : (
          <EmptyState 
            searchQuery={searchQuery}
            hasFilters={hasFilters}
            showToggle={showToggle}
          />
        )}
      </div>

      {/* Fee Detail Modal */}
      {showFeeModal && selectedBrokerForFees && (
        <FeeDetailModal
          isOpen={showFeeModal}
          onClose={() => setShowFeeModal(false)}
          broker={selectedBrokerForFees}
        />
      )}
    </div>
  );
};